import React, { useState } from 'react'
import { Box, Typography, Collapse } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'

const faqs = [
  {
    summary: 'What is SpotPool?',
    description: 'SpotPool is an innovative automated solution for pool table management. It revolutionizes the way pool table owners operate their businesses by eliminating the need for staff, outdated coin systems, and manual supervision.'
  },
  {
    summary: 'How does the SpotPool app work?',
    description: 'With the SpotPool app, owners manage their tables remotely. Clients pay for game time directly through the app, which automatically unlocks the pool table for the purchased rounds — no coins, no keys, no manual intervention.'
  },
  {
    summary: 'What are the main benefits?',
    description: 'SpotPool streamlines pool table management by enabling remote control, automating payments and table unlocking, and providing real-time analytics. It eliminates overhead, boosts efficiency, and ensures a seamless experience for owners and players alike.'
  },
  {
    summary: 'How do I get SpotPool installed?',
    description: 'Simply reach out via our website or contact page to discuss your needs. We\'ll schedule a convenient installation time, set up the hardware and software, and provide training on the SpotPool app. You\'ll be up and running in no time.'
  },
  {
    summary: 'Is my data secure?',
    description: 'Absolutely. SpotPool uses industry-standard encryption for all transactions and data storage. Your business data and your customers\' payment information are protected at every step.'
  },
  {
    summary: 'Can I manage multiple pool tables?',
    description: 'Yes. SpotPool is built to scale. Whether you have one table or fifty, the dashboard gives you a unified view of all your devices, revenue, and game activity in real time.'
  }
]

const Faqs = () => {
  const [expanded, setExpanded] = useState<number | null>(null)

  return (
    <Box
      id='faqs'
      sx={{
        py: { xs: 8, md: 14 },
        px: { xs: 3, md: 10 },
        bgcolor: '#fff'
      }}
    >
      {/* Section Header */}
      <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 10 } }}>
        <Typography
          data-aos="fade-down"
          sx={{ color: 'primary.main', fontWeight: 700, fontSize: 13, letterSpacing: 2, textTransform: 'uppercase', mb: 1 }}
        >
          Got Questions?
        </Typography>
        <Typography
          variant='h3'
          data-aos="fade-up"
          sx={{ fontWeight: 800, color: '#0d1117', fontSize: { xs: '1.8rem', md: '2.4rem' } }}
        >
          Frequently Asked Questions
        </Typography>
      </Box>

      {/* FAQ List */}
      <Box sx={{ maxWidth: 760, mx: 'auto', display: 'flex', flexDirection: 'column', gap: 0 }}>
        {faqs.map((faq, i) => {
          const open = expanded === i
          return (
            <Box
              key={i}
              data-aos="fade-up"
              data-aos-delay={i * 60}
              onClick={() => setExpanded(open ? null : i)}
              sx={{
                borderBottom: '1px solid #e5e7eb',
                cursor: 'pointer',
                py: 3,
                px: 1,
                '&:first-of-type': { borderTop: '1px solid #e5e7eb' },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: '0.75rem',
                      color: open ? 'primary.main' : '#d1d5db',
                      mt: '3px',
                      minWidth: 24,
                      transition: 'color 0.2s'
                    }}
                  >
                    {String(i + 1).padStart(2, '0')}
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: { xs: '0.95rem', md: '1.05rem' },
                      color: open ? '#0d1117' : '#374151',
                      transition: 'color 0.2s',
                      lineHeight: 1.5
                    }}
                  >
                    {faq.summary}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    flexShrink: 0,
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: open ? 'primary.main' : '#f3f4f6',
                    color: open ? '#fff' : '#6b7280',
                    transition: 'all 0.2s',
                    mt: '1px'
                  }}
                >
                  {open ? <RemoveIcon sx={{ fontSize: 16 }} /> : <AddIcon sx={{ fontSize: 16 }} />}
                </Box>
              </Box>

              <Collapse in={open} timeout={220}>
                <Box sx={{ pl: { xs: 0, sm: '36px' }, pt: 2 }}>
                  <Typography
                    sx={{
                      color: '#6b7280',
                      fontSize: '0.95rem',
                      lineHeight: 1.85
                    }}
                  >
                    {faq.description}
                  </Typography>
                </Box>
              </Collapse>
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}

export default Faqs
